import { useState, useEffect, useRef } from "react";
import ModalShell from "./ModalShell";
import { CALL_TAG_OPTIONS, CALL_TAG_CONFIG } from "@constants/callTags";
import { sanitizeFormValues } from "@utils/sanitize";

const STATUS_OPTIONS = [
  "NEW_CALL",
  "PENDING",
  "HANDLED",
  "RESOLVED",
  "DISCARD",
  "HANDLED_BY_AI",
];

const FIELDS = [
  { key: "patient_name", label: "Patient Name", type: "text" },
  { key: "phone_number", label: "Phone Number", type: "text" },
  { key: "callback_phone_number", label: "Callback Phone", type: "text" },
  {
    key: "call_tag",
    label: "Call Tag",
    type: "select",
    options: CALL_TAG_OPTIONS,
    optionLabel: (opt) => CALL_TAG_CONFIG[opt]?.label ?? opt,
  },
  { key: "reason_for_visit", label: "Call Summary", type: "textarea" },
  { key: "insurance_provider", label: "Insurance Provider", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "session_type", label: "Session Type", type: "text" },
  { key: "status", label: "Status", type: "select", options: STATUS_OPTIONS },
  { key: "action_notes", label: "Action Notes", type: "textarea" },
];

const INPUT_CLASS =
  "w-full rounded-xl border border-gray-300 px-4 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-shadow disabled:opacity-50";

export default function EditAppointmentModal({
  isOpen,
  onClose,
  appointment,
  onSave,
  isSaving = false,
  error = null,
}) {
  const [form, setForm] = useState({});
  const [original, setOriginal] = useState({});
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && appointment) {
      const initial = {};
      for (const f of FIELDS) {
        initial[f.key] = appointment[f.key] ?? "";
      }
      setForm(initial);
      setOriginal(initial);
      setTimeout(() => firstInputRef.current?.focus(), 100);
    }
  }, [isOpen, appointment]);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    if (isSaving) return;
    const changed = {};
    for (const f of FIELDS) {
      if (form[f.key] !== original[f.key]) {
        changed[f.key] = form[f.key];
      }
    }
    onSave(sanitizeFormValues(changed));
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      isSaving={isSaving}
      error={error}
      title="Edit Appointment"
      appointment={appointment}
      saveLabel="Save Changes"
      onSubmit={handleSave}
      bodyClassName="max-h-[60vh] overflow-y-auto"
    >
      <div className="space-y-4">
        {FIELDS.map((field, idx) => (
          <div key={field.key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            {field.type === "textarea" ? (
              <textarea
                value={form[field.key] ?? ""}
                onChange={(e) => handleChange(field.key, e.target.value)}
                disabled={isSaving}
                rows={3}
                className={`${INPUT_CLASS} resize-none`}
              />
            ) : field.type === "select" ? (
              <select
                value={form[field.key] ?? ""}
                onChange={(e) => handleChange(field.key, e.target.value)}
                disabled={isSaving}
                className={INPUT_CLASS}
              >
                {field.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {field.optionLabel
                      ? field.optionLabel(opt)
                      : opt.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            ) : (
              <input
                ref={idx === 0 ? firstInputRef : undefined}
                type="text"
                value={form[field.key] ?? ""}
                onChange={(e) => handleChange(field.key, e.target.value)}
                disabled={isSaving}
                className={INPUT_CLASS}
              />
            )}
          </div>
        ))}
      </div>
    </ModalShell>
  );
}
